<template>
  <b-modal
    :visible="visible"
    size="lg"
    centered
    scrollable
    :title="$t('ia.contents.referencesLibrary')"
    @show="onShow"
    @hidden="$emit('close')"
  >
    <div class="library-filters">
      <button
        v-for="option in kind_options"
        :key="option.value || 'all'"
        type="button"
        class="library-filter"
        :class="{ active: kind === option.value }"
        @click="changeKind(option.value)"
      >{{ option.text }}</button>
    </div>

    <div v-if="loading && references.length === 0" class="library-grid">
      <b-skeleton-img v-for="index in 8" :key="index" no-aspect height="120px"></b-skeleton-img>
    </div>
    <p v-else-if="loaded && references.length === 0" class="text-center text-muted avenir-medium py-3 mb-0">
      {{ $t('ia.contents.referencesLibraryEmpty') }}
    </p>
    <template v-else>
      <div class="library-grid">
        <button
          v-for="reference in references"
          :key="reference.uuid"
          type="button"
          class="library-item"
          :class="{ selected: isSelected(reference), disabled: !isSelected(reference) && selection.length >= max }"
          :title="reference.name"
          @click="toggle(reference)"
        >
          <img :src="reference.url" alt="" />
          <span class="library-check"><feather-icon icon="CheckIcon" size="14"></feather-icon></span>
          <span class="library-kind">{{ $t(`ia.contents.referenceKinds.${reference.kind}`) }}</span>
        </button>
      </div>
      <div v-if="next_page" class="text-center mt-1">
        <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="getReferences(next_page)">
          <b-spinner v-if="loading" small class="mr-50"></b-spinner>{{ $t('ia.contents.loadMore') }}
        </b-button>
      </div>
    </template>

    <template #modal-footer>
      <small class="text-muted mr-auto">{{ $t('ia.contents.referencesSelected', { count: selection.length, max }) }}</small>
      <b-button variant="outline-secondary" @click="$emit('close')">{{ $t('ia.contents.cancel') }}</b-button>
      <b-button variant="primary" @click="$emit('apply', selection)">{{ $t('ia.contents.useReferences') }}</b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BSpinner,
  BSkeletonImg,
} from "bootstrap-vue";
import { getContentReferences } from '@/services/ai-contents';
import { showToast } from '@/libs/utils/others';
import { REFERENCE_KINDS } from './contents-utils';

export default {
  name: 'ReferencesLibrary',
  components: {
    BModal,
    BButton,
    BSpinner,
    BSkeletonImg,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // References already chosen for the batch
    selected: {
      type: Array,
      default: () => [],
    },
    max: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      references: [],
      selection: [],
      kind: null,
      loading: false,
      loaded: false,
      next_page: null,
    };
  },
  computed: {
    kind_options() {
      return [
        { value: null, text: this.$t('ia.contents.allKinds') },
        ...REFERENCE_KINDS.map((kind) => ({ value: kind, text: this.$t(`ia.contents.referenceKinds.${kind}`) })),
      ];
    },
  },
  methods: {
    onShow() {
      this.selection = [...this.selected];
      this.getReferences();
    },
    changeKind(kind) {
      this.kind = kind;
      this.getReferences();
    },
    getReferences(page = 1) {
      const params = { page };
      if (this.kind) params.kind = this.kind;
      const kind = this.kind;

      this.loading = true;
      getContentReferences(params).then((response) => {
        if (kind !== this.kind) return;
        const results = response.results || [];
        this.references = page === 1 ? results : this.references.concat(results);
        this.next_page = response.next ? page + 1 : null;
      }).catch(() => {
        showToast(this, 'danger', 'AlertCircleIcon', 'ia.contents.errorReferences');
      }).finally(() => {
        this.loading = false;
        this.loaded = true;
      });
    },
    isSelected(reference) {
      return this.selection.some((item) => item.uuid === reference.uuid);
    },
    toggle(reference) {
      if (this.isSelected(reference)) {
        this.selection = this.selection.filter((item) => item.uuid !== reference.uuid);
      } else if (this.selection.length < this.max) {
        this.selection = [...this.selection, reference];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.library-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;

  .library-filter {
    padding: 0.3rem 0.85rem;
    border: 1px solid #d8d6de;
    border-radius: 2rem;
    background-color: #fff;
    color: #6e6b7b;
    font-size: 0.85rem;
    cursor: pointer;

    &.active {
      border-color: #7367f0;
      background-color: rgba(115, 103, 240, 0.12);
      color: #7367f0;
    }
  }
}
.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}
.library-item {
  position: relative;
  aspect-ratio: 1 / 1;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #f3f2f7;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .library-check {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    display: none;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: #7367f0;
    color: #fff;
  }
  .library-kind {
    position: absolute;
    left: 0.4rem;
    bottom: 0.4rem;
    padding: 0.1rem 0.45rem;
    border-radius: 0.4rem;
    background-color: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 0.7rem;
  }
  &.selected {
    border-color: #7367f0;

    .library-check {
      display: flex;
    }
  }
  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
</style>
